import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Tv, ArrowRight, TrendingUp, Lock } from "lucide-react";
import SignInModal from "@/components/SignInModal";
import { useAuth } from "@/lib/auth";

const HERO_IMG =
  "https://images.unsplash.com/photo-1522778526097-ce0a22ceb253?auto=format&fit=crop&w=1600&q=70";

const TIERS = [
  {
    key: "guide",
    name: "Guide",
    useCase: "Find your game",
    priceMonthly: "$1.99",
    priceYearly: "$19.99",
    yearlySavings: "Save 16%",
    tagline: "The schedule, plus the story on every matchup.",
    cta: "Coming soon",
    features: [
      "Full sports schedule (NFL, NBA, MLB, NHL, MLS, CFB & more)",
      "Streaming service badges on every game",
      "Timezone + date range picker",
      "Add-to-calendar (.ics) export",
      "Favorite teams + streamers memory",
      "AI Match Insights — editorial preview on every game",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    useCase: "Find your fantasy",
    priceMonthly: "$7.99",
    priceYearly: "$59.99",
    yearlySavings: "Save 37%",
    tagline: "Everything in Guide, plus start/sit for every slate.",
    cta: "Coming soon",
    features: [
      "Everything in Guide",
      "AI Fantasy Insights — START / FADE / INJURY WATCH per game",
      "Reads player-prop markets + ESPN official injuries + weather",
      "Refresh-on-demand before lineup lock",
      "NFL + NBA + MLB + NHL",
    ],
  },
  {
    key: "edge",
    name: "Edge",
    useCase: "Find your bet",
    priceMonthly: "$12.99",
    priceYearly: "$99.99",
    yearlySavings: "Save 36%",
    tagline: "Everything in Pro, plus the sharp side of the line.",
    cta: "Coming soon",
    features: [
      "Everything in Pro",
      "AI Betting Insights — angles, trends, line context",
      "Parlay Builder with Kelly sizing & Gaussian Copula pricing",
      "Player Props across moneyline/spread/total markets",
      "Edge alerts — only when model vs. market diverges",
      "Shareable, deep-linked parlay slips",
    ],
  },
];

export default function Landing() {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInMode, setSignInMode] = useState("signin");

  function openSignIn(mode = "signin") {
    setSignInMode(mode);
    setSignInOpen(true);
  }

  async function handleLogout() {
    await logout();
    // Hard reload to "/" so no stale state can hide the Sign-in button —
    // AuthProvider re-mounts fresh and /auth/me returns 401 cleanly.
    window.location.assign("/");
  }

  const authed = status === "in" && user;

  return (
    <main className="min-h-screen w-full bg-[#fafaf9] text-[#111]" data-testid="landing-page">
      {/* Marquee strip */}
      <div className="border-b-2 border-[#111] bg-[#111] text-[#f6f6f4] overflow-hidden">
        <div className="marquee py-2">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center shrink-0">
              {["LIVE SPORTS", "STREAMING SCHEDULE", "WHERE TO WATCH", "POWERED BY AI", "SPORTS BROADSHEET", "EDITION 01"].map(
                (t, i) => (
                  <span key={`${k}-${i}`} className="px-8 font-mono text-xs tracking-[0.4em] uppercase">
                    {t} <span className="opacity-50 ml-8">/</span>
                  </span>
                ),
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Top nav */}
      <nav className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="font-display text-2xl font-extrabold tracking-tight" data-testid="landing-logo">
            guidedsport<span className="opacity-60">.</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/tracker"
              className="hidden md:inline-flex font-mono text-xs tracking-[0.25em] uppercase font-bold link-u text-[#111]"
              data-testid="landing-nav-schedule"
            >
              Schedule
            </Link>
            {authed ? (
              <div className="flex items-center gap-3" data-testid="landing-user-chip">
                <span className="hidden sm:inline font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#555]">
                  {user.email}
                </span>
                <button
                  type="button"
                  onClick={handleLogout}
                  data-testid="landing-signout-btn"
                  className="font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 border-[#111] px-3 py-1.5 hover:bg-[#111] hover:text-[#f6f6f4] transition-colors focus-ring active:translate-y-px"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => openSignIn("signin")}
                data-testid="landing-signin-btn"
                className="font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 border-[#111] px-3 py-1.5 hover:bg-[#111] hover:text-[#f6f6f4] transition-colors focus-ring active:translate-y-px"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-10 pb-12">
          <div className="flex items-center gap-3">
            <span className="bullet" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
              Vol. 01 · {new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }).toUpperCase()}
            </span>
          </div>

          <h1 className="tracker-logo mt-6 text-[14vw] md:text-[9vw] leading-[0.85]" data-testid="landing-wordmark">
            guidedsport<span className="inline-block ml-1">.</span>
          </h1>

          <div className="mt-8 grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                Every game. Every streamer. Every edge.
              </p>
              <p className="mt-4 text-[#555] max-w-2xl text-base md:text-lg">
                guidedsport scans the open web, pulls a clean schedule, tells you exactly
                which streamer carries each game — and runs the numbers so you don't have to.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/tracker"
                  className="btn-block focus-ring"
                  data-testid="landing-cta-tracker"
                >
                  <span className="inline-flex items-center gap-3">
                    See the schedule
                    <ArrowRight size={18} strokeWidth={2.6} />
                  </span>
                </Link>
                {!authed && (
                  <button
                    type="button"
                    onClick={() => openSignIn("signup")}
                    data-testid="landing-cta-signup"
                    className="inline-flex items-center gap-3 border-2 border-[#111] bg-transparent px-5 py-3 font-display font-extrabold uppercase tracking-[0.12em] text-sm md:text-base hover:bg-[#111] hover:text-[#f6f6f4] transition-colors focus-ring active:translate-y-px"
                  >
                    Create account
                  </button>
                )}
              </div>
              <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#777] font-bold">
                Annual plans save up to 50%
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="border-2 border-[#111] overflow-hidden">
                <img src={HERO_IMG} alt="Stadium" className="w-full h-56 md:h-64 object-cover grayscale" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Value props */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14 border-b-2 border-[#111]">
        <div className="flex items-center gap-3 mb-6">
          <span className="bullet" />
          <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
            02 — What you get
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: <Tv size={20} strokeWidth={2.4} />,
              title: "Never miss a game",
              body: "Live sport-by-sport schedule with the exact streamer carrying each matchup — ESPN+, Peacock, Max, Paramount+, Prime, Fubo, YouTube TV, DirecTV Stream.",
            },
            {
              icon: <TrendingUp size={20} strokeWidth={2.4} />,
              title: "Edges, not noise",
              body: "Our model trains nightly on real box scores and lines. We only surface a bet when it diverges from the market — no stale takes, no screaming hype.",
            },
            {
              icon: <Lock size={20} strokeWidth={2.4} />,
              title: "Your account, your account",
              body: "Email + password or one-click Google. No credit card to try us. Upgrade only when the edges are worth the price of a sandwich.",
            },
          ].map((v) => (
            <div key={v.title} className="border-2 border-[#111] p-5 bg-[#fafaf9]">
              <div className="inline-flex items-center justify-center w-9 h-9 border-2 border-[#111]">
                {v.icon}
              </div>
              <h3 className="mt-4 font-display text-xl font-extrabold">{v.title}</h3>
              <p className="mt-2 text-sm text-[#555] leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14 border-b-2 border-[#111]" data-testid="pricing-section">
        <div className="flex items-center gap-3 mb-2">
          <span className="bullet" />
          <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
            03 — Pricing
          </h2>
        </div>
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <p className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight max-w-2xl">
            Three plans. Pick the one that matches how deep you want to go.
          </p>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#777] font-bold">
            Pay yearly and save up to 50% · Billing coming soon
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((t) => (
            <div
              key={t.key}
              data-testid={`pricing-tier-${t.key}`}
              className="relative border-2 border-[#111] p-6 flex flex-col"
              style={{ background: "#111", color: "#f6f6f4" }}
            >
              <div className="absolute -top-3 left-6 px-2 py-0.5 bg-[#f6f6f4] text-[#111] border-2 border-[#111] font-mono text-[10px] tracking-[0.25em] uppercase font-extrabold">
                {t.useCase}
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold text-[#aaa] mt-1">
                {t.name}
              </div>

              {/* Monthly price = hero. Yearly sits below on its own line so it's actually readable. */}
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-5xl font-extrabold tracking-tight text-[#f6f6f4]">
                  {t.priceMonthly}
                </span>
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase font-bold text-[#aaa]">
                  /month
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm md:text-base tracking-[0.12em] uppercase font-extrabold text-[#f6f6f4]">
                  or {t.priceYearly}/year
                </span>
                <span className="inline-flex items-center px-2 py-0.5 bg-[#f6f6f4] text-[#0a0a0a] font-mono text-[10px] tracking-[0.2em] uppercase font-extrabold">
                  {t.yearlySavings}
                </span>
              </div>

              <p className="mt-4 text-sm text-[#ddd]">
                {t.tagline}
              </p>

              <ul className="mt-5 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm leading-relaxed">
                    <Check
                      size={14}
                      strokeWidth={2.8}
                      className="mt-1 shrink-0"
                      style={{ color: "#4ade80" }}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                <button
                  type="button"
                  disabled
                  data-testid={`pricing-cta-${t.key}`}
                  className="w-full inline-flex items-center justify-center gap-3 border-2 border-dashed border-[#aaa] text-[#aaa] px-4 py-3 font-display font-extrabold uppercase tracking-[0.12em] text-sm cursor-not-allowed"
                >
                  {t.cta}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14">
        <div className="border-2 border-[#111] p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#555] font-bold">
              Ready to see tonight's slate?
            </div>
            <p className="mt-3 font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight max-w-2xl">
              Create an account and get on the Guide waitlist.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {!authed ? (
              <button
                type="button"
                onClick={() => openSignIn("signup")}
                data-testid="landing-closing-cta"
                className="btn-block focus-ring"
              >
                <span className="inline-flex items-center gap-3">
                  Create account
                  <ArrowRight size={18} strokeWidth={2.6} />
                </span>
              </button>
            ) : (
              <Link
                to="/tracker"
                data-testid="landing-closing-cta"
                className="btn-block focus-ring"
              >
                <span className="inline-flex items-center gap-3">
                  See the schedule
                  <ArrowRight size={18} strokeWidth={2.6} />
                </span>
              </Link>
            )}
            {!authed && (
              <button
                type="button"
                onClick={() => openSignIn("signin")}
                className="inline-flex items-center gap-3 border-2 border-[#111] bg-transparent px-5 py-3 font-display font-extrabold uppercase tracking-[0.12em] text-sm hover:bg-[#111] hover:text-[#f6f6f4] transition-colors focus-ring active:translate-y-px"
                data-testid="landing-closing-signin"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-[0.25em] text-[#555] font-bold">
          <div>© {new Date().getFullYear()} guidedsport. Independent, ad-light, edge-forward.</div>
          <div className="flex items-center gap-4">
            <Link to="/tracker" className="link-u text-[#111]">Schedule</Link>
            {!authed && (
              <button type="button" onClick={() => openSignIn("signin")} className="link-u text-[#111]">
                Sign in
              </button>
            )}
          </div>
        </div>
      </footer>

      <SignInModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        defaultMode={signInMode}
      />
    </main>
  );
}
